import React, { useRef, useState, useEffect } from "react";
import { Modal, Card, Divider, Box, Typography, IconButton, Tooltip, Icon } from "@mui/material";
import MDButton from "components/MDButton";
// Alerts are now handled by parent via onShowAlert callback
import MDBox from "components/MDBox";
import jsQR from "jsqr";
import CameraAltIcon from "@mui/icons-material/CameraAlt";

// Componente reutilizable que abre la cámara, escanea QR y devuelve el texto escaneado
// Props:
// - onDetected(scanned: string) => void   (obligatorio)
// - buttonLabel: texto para el botón que abre el escáner (opcional)
// - dialogTitle: título del diálogo (opcional)
export default function QRScanner({
  onDetected,
  buttonLabel = "Abrir cámara (escáner QR)",
  dialogTitle = "Escanear QR",
  iconOnly = false,
  tooltip = "Verificar nueva invitación",
  icon = null,
  // new: parent can provide a handler to display alerts outside the modal
  onShowAlert,
}) {
  // estado y refs principales
  const [open, setOpen] = useState(false);
  const [scanning, setScanning] = useState(false);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const animationRef = useRef(null);
  const [torchAvailable, setTorchAvailable] = useState(false);
  const [torchOn, setTorchOn] = useState(false);
  const [showHighlight, setShowHighlight] = useState(false);
  const highlightTimeoutRef = useRef(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMsg, setSnackbarMsg] = useState("");
  const [cameraError, setCameraError] = useState(null);
  // alert state removed; parent should render MDAlert

  const showError = (msg) => {
    // Map camera errors to the project's MDAlert pattern
    setSnackbarMsg(msg);
    setCameraError(msg);
    if (typeof onShowAlert === "function") onShowAlert("fail", msg);
  };

  useEffect(() => {
    return () => {
      stopCamera();
    };
  }, []);

  // NOTE: Changed behavior: do NOT auto-close the modal when camera access fails.
  // Instead we show an inline message and system snackbar. User can retry or close the modal manually.

  const startCamera = async () => {
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      const msg = "El navegador no soporta acceso a la cámara.";
      console.warn(msg);
      showError(msg);
      return;
    }
    // check if any video input devices are available before requesting permissions
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const hasVideoInput = devices.some((d) => d.kind === "videoinput");
      if (!hasVideoInput) {
        const msg = "No se detectó ninguna cámara en este dispositivo.";
        console.warn(msg);
        showError(msg);
        return;
      }
    } catch (e) {
      // ignore enumeration errors and proceed to request permission
    }
    try {
      setScanning(true);
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: "environment" } },
        audio: false,
      });
      streamRef.current = stream;
      // clear any previous camera error once we have a stream
      setCameraError(null);
      // detect torch availability on the video track
      try {
        const [track] = stream.getVideoTracks();
        const capabilities = track.getCapabilities && track.getCapabilities();
        if (capabilities && capabilities.torch) setTorchAvailable(true);
      } catch (e) {
        // ignore
      }
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
      scanFrame();
    } catch (err) {
      console.error("getUserMedia error:", err);
      // show friendly error to the user
      const msg =
        err && err.name === "NotAllowedError"
          ? "Permiso denegado para usar la cámara. Habilita los permisos del navegador."
          : "No se pudo acceder a la cámara en este dispositivo.";
      showError(msg);
      setScanning(false);
      // DO NOT close the modal automatically. Keep it open so user can read the error and retry.
    }
  };

  const stopCamera = () => {
    setScanning(false);
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
      animationRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      try {
        videoRef.current.pause();
        videoRef.current.srcObject = null;
      } catch (e) {
        // ignore
      }
    }
  };

  const scanFrame = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) {
      animationRef.current = requestAnimationFrame(scanFrame);
      return;
    }
    const width = video.videoWidth;
    const height = video.videoHeight;
    if (width === 0 || height === 0) {
      animationRef.current = requestAnimationFrame(scanFrame);
      return;
      // Componente reutilizable que abre la cámara, escanea QR y devuelve el texto escaneado
      // Props:
      // - onDetected(scanned: string) => void   (obligatorio)
      // - buttonLabel: texto para el botón que abre el escáner (opcional)
      // - dialogTitle: título del diálogo (opcional)
    }
    // Use a center-cropped ROI to focus scanning and improve performance
    // estado y refs principales
    const roiSize = Math.min(width, height) * 0.75; // 75% of the smallest side (more focus)
    const sx = Math.floor((width - roiSize) / 2);
    const sy = Math.floor((height - roiSize) / 2);
    canvas.width = roiSize;
    canvas.height = roiSize;
    const ctx = canvas.getContext("2d");
    // draw only the ROI from the video
    ctx.drawImage(video, sx, sy, roiSize, roiSize, 0, 0, roiSize, roiSize);
    const imageData = ctx.getImageData(0, 0, roiSize, roiSize);
    try {
      const code = jsQR(imageData.data, roiSize, roiSize, { inversionAttempts: "attemptBoth" });
      if (code && code.data) {
        const scanned = (code.data || "").trim();
        // mostrar un breve resaltado azul sobre la ROI central como retroalimentación de enfoque
        setShowHighlight(true);
        // mantener el resaltado visible brevemente para que el usuario vea la retroalimentación
        if (highlightTimeoutRef.current) clearTimeout(highlightTimeoutRef.current);
        highlightTimeoutRef.current = setTimeout(() => {
          // detener cámara y cerrar
          stopCamera();
          setOpen(false);
          if (typeof onDetected === "function") onDetected(scanned);
          setShowHighlight(false);
          highlightTimeoutRef.current = null;
        }, 450);
        return;
      }
    } catch (e) {
      console.error("jsQR error:", e);
    }
    animationRef.current = requestAnimationFrame(scanFrame);
  };

  const handleOpen = () => {
    setOpen(true);
    // pequeño delay para que el diálogo renderice antes de iniciar la cámara
    setTimeout(() => startCamera(), 150);
  };

  const handleClose = () => {
    // clear any pending highlight timeout
    if (highlightTimeoutRef.current) {
      clearTimeout(highlightTimeoutRef.current);
      highlightTimeoutRef.current = null;
    }
    stopCamera();
    // limpiar estado de error de cámara y la alerta cuando el usuario cierra el diálogo
    setCameraError(null);
    setSnackbarOpen(false);
    setSnackbarMsg("");
    setOpen(false);
  };

  const style = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "90%",
    maxWidth: "600px",
    borderRadius: "20px",
    boxShadow: 24,
    p: 4,
  };

  return (
    <>
      {iconOnly ? (
        <Tooltip title={tooltip} placement="top">
          <IconButton onClick={handleOpen} color="primary" size="large">
            {icon ? icon : <CameraAltIcon />}
          </IconButton>
        </Tooltip>
      ) : (
        <MDButton onClick={handleOpen} color="info">
          {buttonLabel}
        </MDButton>
      )}

      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Card sx={style}>
          <MDBox
            mt={-7}
            color="white"
            bgColor="info"
            variant="gradient"
            borderRadius="lg"
            p={2}
            sx={{ display: "flex", justifyContent: "flex-end" }}
          >
            <Typography id="modal-modal-title" component="h2">
              {dialogTitle}
            </Typography>
            <Icon
              sx={{
                fontSize: "2em",
                stroke: "currentColor",
                strokeWidth: "2px",
                cursor: "pointer",
                marginLeft: "auto",
                color: "white",
              }}
              onClick={handleClose}
            >
              close
            </Icon>
          </MDBox>
          <Divider variant="middle" />

          <Box sx={{ p: 2 }}>
            <div style={{ position: "relative", textAlign: "center" }}>
              <video
                ref={videoRef}
                style={{ width: "100%", height: "auto", borderRadius: 4 }}
                playsInline
                muted
              />
              <canvas ref={canvasRef} style={{ display: "none" }} />
              {showHighlight && (
                <div
                  aria-hidden
                  style={{
                    position: "absolute",
                    left: "50%",
                    top: "50%",
                    transform: "translate(-50%,-50%)",
                    width: "70%",
                    maxWidth: 420,
                    aspectRatio: "1 / 1",
                    borderRadius: 8,
                    boxShadow: "0 0 0 10px rgba(25,118,210,0.28)",
                    border: "3px solid rgba(25,118,210,0.95)",
                    pointerEvents: "none",
                    transition: "opacity 220ms ease, transform 180ms ease",
                  }}
                />
              )}
            </div>

            <div
              style={{
                marginTop: 8,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <div style={{ textAlign: "left" }}>
                <strong style={{ fontSize: 13 }}>{scanning ? "Enfoca el QR" : ""}</strong>
                <div style={{ fontSize: 11, color: "#666" }}>
                  Mantén la cámara estable y evita reflejos. Acerca o aleja el dispositivo hasta que
                  el recuadro marque el QR.
                </div>
              </div>
              <div />
            </div>
          </Box>

          <Divider variant="middle" />
          <Box sx={{ display: "flex", justifyContent: "flex-end", p: 1 }}>
            <MDButton onClick={handleClose} color="error">
              Cerrar
            </MDButton>
          </Box>
        </Card>
      </Modal>
      {/* Alerts are handled by parent via onShowAlert callback */}
    </>
  );
}
